import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { FiRefreshCw } from "react-icons/fi";
import useAuth from "../../../hooks/useAuth";
import useSocket from "../../../hooks/useSocket";
import { fieldTrackingService } from "../../../services/fieldTracking.service";
import { canViewFleetMap } from "../../../utils/fieldTrackingPermissions";
import { displayUserName } from "../../../utils/displayUserName";
import { formatRepoRole } from "../../../constants/repoRoles";
import FieldMapView from "../../../components/tracking/FieldMapView";
import LocationPermissionModal from "../../../components/tracking/LocationPermissionModal";
import "../../../styles/fieldMap.css";
import "../../../styles/users.css";

import {
  isLocationPermissionDismissed,
  isLocationPermissionGranted,
  markLocationPermissionDismissed,
  markLocationPermissionGranted,
} from "../../../constants/locationPermission";

function mergeTracer(list, incoming) {
  if (!incoming?.tracerId) return list;
  const idx = list.findIndex((t) => t.tracerId === incoming.tracerId);
  const row = {
    tracerId: incoming.tracerId,
    name: displayUserName({
      name: incoming.tracerName || incoming.name,
      role: incoming.role,
    }),
    phone: incoming.phone || "",
    post: incoming.post || "",
    role: incoming.role || "REPO_STAFF",
    latitude: incoming.latitude,
    longitude: incoming.longitude,
    accuracy: incoming.accuracy,
    updatedAt: incoming.updatedAt || new Date().toISOString(),
  };
  if (idx === -1) return [row, ...list];
  const next = [...list];
  next[idx] = { ...next[idx], ...row };
  return next;
}

export default function FieldMap() {
  const { auth } = useAuth();
  const role = auth?.user?.role;
  const [searchParams, setSearchParams] = useSearchParams();
  const agentId = searchParams.get("agentId") || "";

  const [tracers, setTracers] = useState([]);
  const [viewerLocation, setViewerLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshedAt, setRefreshedAt] = useState(null);

  const [permissionOpen, setPermissionOpen] = useState(false);
  const [permissionBusy, setPermissionBusy] = useState(false);
  const [permissionError, setPermissionError] = useState("");

  const loadTracers = useCallback(async () => {
    if (!auth?.token) return;
    try {
      setLoading(true);
      setError("");
      const res = await fieldTrackingService.getLiveTracers(auth.token);
      const items = res?.data?.items || [];
      setTracers(items);
      setRefreshedAt(res?.data?.refreshedAt || new Date().toISOString());
    } catch (err) {
      setError(err?.response?.data?.message || "Could not load tracer locations.");
    } finally {
      setLoading(false);
    }
  }, [auth?.token]);

  const onSocketEvent = useCallback((event, payload) => {
    if (event === "tracer:location" && payload) {
      setTracers((prev) => mergeTracer(prev, payload));
      setRefreshedAt(new Date().toISOString());
    }
  }, []);

  useSocket(onSocketEvent);

  useEffect(() => {
    loadTracers();
    const id = window.setInterval(loadTracers, 15000);
    return () => window.clearInterval(id);
  }, [loadTracers]);

  useEffect(() => {
    if (!canViewFleetMap(role)) return;
    if (
      isLocationPermissionDismissed() ||
      isLocationPermissionGranted() ||
      viewerLocation
    ) {
      return;
    }
    setPermissionOpen(true);
  }, [role, viewerLocation]);

  const requestViewerLocation = () => {
    if (!navigator.geolocation) {
      setPermissionError("Geolocation is not supported on this device.");
      return;
    }
    setPermissionBusy(true);
    setPermissionError("");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        setViewerLocation({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
        try {
          await fieldTrackingService.postMyLocation(
            {
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
              accuracy: pos.coords.accuracy,
            },
            auth.token
          );
          window.dispatchEvent(new CustomEvent("repo:location-granted"));
        } catch {
          /* map still shows local marker */
        }
        setPermissionOpen(false);
        setPermissionBusy(false);
        markLocationPermissionGranted();
      },
      () => {
        setPermissionError(
          "Location blocked. Enable location in browser settings to see yourself on the map."
        );
        setPermissionBusy(false);
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const dismissPermission = () => {
    markLocationPermissionDismissed();
    setPermissionOpen(false);
    setPermissionError("");
  };

  const openAgent = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set("agentId", String(id));
    setSearchParams(next);
  };

  const backToFieldMap = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("agentId");
    setSearchParams(next);
  };

  const selectedAgent = useMemo(
    () => tracers.find((t) => String(t.tracerId) === String(agentId)) || null,
    [tracers, agentId]
  );

  if (!canViewFleetMap(role)) {
    return <Navigate to="/live-tracking" replace />;
  }

  const isTeamLeader = role === "TEAM_LEADER";

  if (agentId) {
    const lat = selectedAgent ? Number(selectedAgent.latitude) : NaN;
    const lng = selectedAgent ? Number(selectedAgent.longitude) : NaN;
    const hasFix = Number.isFinite(lat) && Number.isFinite(lng);

    return (
      <div className="page field-map-page">
        <div className="create-company-header">
          <div>
            <h2>Live agent location</h2>
            <p className="muted">Current GPS for the selected user. Updates automatically.</p>
          </div>
          <div className="create-company-actions">
            <button type="button" className="secondary-page-btn" onClick={backToFieldMap}>
              Back to Field Map
            </button>
          </div>
        </div>

        {error ? <p className="error-text">{error}</p> : null}

        {!loading && !selectedAgent ? (
          <div className="create-company-card">
            <p className="muted">
              This agent is not currently live, or their location is older than 24 hours.
            </p>
            <button type="button" className="primary-page-btn" onClick={backToFieldMap}>
              Back to Field Map
            </button>
          </div>
        ) : (
          <div className="create-company-card field-agent-live-card">
            <h3>{selectedAgent?.name || "Agent"}</h3>
            <p className="muted" style={{ marginTop: 0 }}>
              {formatRepoRole(selectedAgent?.role)}
            </p>
            {hasFix ? (
              <p>
                {lat.toFixed(5)}, {lng.toFixed(5)}
                {selectedAgent?.updatedAt
                  ? ` · Updated ${new Date(selectedAgent.updatedAt).toLocaleString()}`
                  : ""}
              </p>
            ) : (
              <p className="muted">Waiting for live GPS…</p>
            )}
            <div className="field-map-panel">
              <FieldMapView
                tracers={selectedAgent && hasFix ? [selectedAgent] : []}
                selectedTracerId={selectedAgent?.tracerId || null}
                followAgent
              />
            </div>
          </div>
        )}

        <LocationPermissionModal
          open={permissionOpen}
          onAllow={requestViewerLocation}
          onDismiss={dismissPermission}
          busy={permissionBusy}
          error={permissionError}
        />
      </div>
    );
  }

  return (
    <div className="page field-map-page">
      <div className="create-company-header">
        <div>
          <h2>Field map</h2>
          <p className="muted">
            {isTeamLeader
              ? "Live locations of users you can see in this agency."
              : "Live locations of all active users in your agency."}
          </p>
        </div>
        <div className="create-company-actions">
          <button
            type="button"
            className="secondary-page-btn"
            onClick={loadTracers}
            disabled={loading}
          >
            <FiRefreshCw aria-hidden /> Refresh
          </button>
        </div>
      </div>

      {error ? <p className="error-text">{error}</p> : null}
      {refreshedAt ? (
        <p className="field-map-sidebar-meta">
          Last refreshed: {new Date(refreshedAt).toLocaleString()}
        </p>
      ) : null}

      <div className="field-map-layout">
        <div className="field-map-panel">
          <FieldMapView
            tracers={tracers}
            viewerLocation={viewerLocation}
            onSelectTracer={openAgent}
          />
        </div>

        <aside className="field-map-sidebar">
          <h3>Active users ({tracers.length})</h3>
          {loading && tracers.length === 0 ? (
            <p className="field-map-sidebar-meta">Loading…</p>
          ) : null}
          {!loading && tracers.length === 0 ? (
            <div className="field-map-empty">
              No live locations in the last 24 hours. Users must allow location after login.
            </div>
          ) : (
            <ul className="field-map-tracer-list">
              {tracers.map((tracer) => (
                <li key={tracer.tracerId} className="field-map-tracer-row">
                  <button
                    type="button"
                    className="field-map-tracer-item"
                    onClick={() => openAgent(tracer.tracerId)}
                  >
                    <strong>{tracer.name}</strong>
                    <span>{formatRepoRole(tracer.role)}</span>
                    {tracer.latitude != null && tracer.longitude != null ? (
                      <span>
                        {Number(tracer.latitude).toFixed(5)}, {Number(tracer.longitude).toFixed(5)}
                      </span>
                    ) : null}
                    <span>
                      Updated {new Date(tracer.updatedAt).toLocaleString()}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>

      <LocationPermissionModal
        open={permissionOpen}
        onAllow={requestViewerLocation}
        onDismiss={dismissPermission}
        busy={permissionBusy}
        error={permissionError}
      />
    </div>
  );
}
